import { useState } from 'react';
import { useQuery, useMutation } from 'urql';
import { useWorkspace } from '@/stores/workspace.store';
import { useNavigate } from 'react-router-dom';
import { Q_TEMPLATES, Q_PROJECTS, M_CREATE_TEMPLATE, M_DELETE_TEMPLATE } from '@/lib/queries';
import { useUi } from '@/stores/ui.store';

const CATEGORIES = ['general', 'promo', 'engagement', 'educational', 'announcement'];
const PLATFORMS = ['', 'linkedin', 'twitter', 'facebook', 'instagram', 'tiktok', 'youtube', 'threads', 'pinterest'];
const CAT_STYLE: Record<string, string> = {
  general: 'bg-stone-100 text-stone-600', promo: 'bg-amber-50 text-amber-700',
  engagement: 'bg-pink-50 text-pink-700', educational: 'bg-blue-50 text-blue-700',
  announcement: 'bg-violet-50 text-violet-700',
};

export default function TemplatesRoute() {
  const nav = useNavigate();
  const { toast } = useUi();
  const [projects] = useQuery({ query: Q_PROJECTS });
  const [templates, refetch] = useQuery({ query: Q_TEMPLATES });
  const [, createTpl] = useMutation(M_CREATE_TEMPLATE);
  const [, deleteTpl] = useMutation(M_DELETE_TEMPLATE);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'general', platform: '', body: '' });

  const projectId = (useWorkspace.getState().project_id ?? (projects.data?.projects?.find((p:any)=>p.id===useWorkspace.getState().project_id) ?? projects.data?.projects?.[0])?.id);
  const list: any[] = templates.data?.captionTemplates ?? [];
  const detectedVars = Array.from(new Set([...form.body.matchAll(/\{\{(\w+)\}\}/g)].map((m) => m[1])));

  const submit = async () => {
    if (!projectId) { toast('No project found', 'error'); return; }
    if (!form.name.trim() || !form.body.trim()) { toast('Name and body required', 'warning'); return; }
    const r = await createTpl({ input: { projectId, name: form.name, category: form.category, platform: form.platform || null, body: form.body } });
    if (r.error) { toast(r.error.message, 'error'); return; }
    toast('Template created');
    setForm({ name: '', category: 'general', platform: '', body: '' });
    setShowForm(false);
    refetch({ requestPolicy: 'network-only' });
  };

  const use = (tpl: any) => {
    // Hand the body to the editor via sessionStorage; Create reads it if present.
    sessionStorage.setItem('template_body', tpl.body);
    nav('/app/create');
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Templates</h1>
          <p className="text-sm text-stone-500 mt-1">Reusable caption skeletons with <code className="text-violet-600">{'{{placeholders}}'}</code>.</p>
        </div>
        <button onClick={() => setShowForm((s) => !s)} className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium">
          {showForm ? 'Cancel' : '+ New template'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-stone-200 rounded-xl p-5 space-y-3">
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-stone-600">Name</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-stone-600">Category</span>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-stone-600">Platform</span>
              <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                {PLATFORMS.map((p) => <option key={p} value={p}>{p || 'Any'}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-medium text-stone-600">Body</span>
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5}
              placeholder={'🚀 Introducing {{product}}!\n\n{{benefit}}\n\n{{hashtags}}'}
              className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none font-mono" />
            {detectedVars.length > 0 && (
              <p className="text-[11px] text-stone-400 mt-1">Variables: {detectedVars.map((v) => <span key={v} className="text-violet-600">{`{{${v}}}`} </span>)}</p>
            )}
          </label>
          <div className="flex justify-end"><button onClick={submit} className="px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium">Create</button></div>
        </div>
      )}

      {templates.fetching ? (
        <p className="text-stone-500">Loading…</p>
      ) : list.length === 0 ? (
        <div className="bg-white border border-dashed border-stone-300 rounded-xl p-10 text-center text-stone-500">No templates yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((tpl) => (
            <div key={tpl.id} className="bg-white border border-stone-200 rounded-xl p-4 flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-stone-900 truncate">{tpl.name}</span>
                <span className={`text-[10px] uppercase font-medium px-2 py-0.5 rounded ${CAT_STYLE[tpl.category] ?? 'bg-stone-100 text-stone-600'}`}>{tpl.category}</span>
              </div>
              <pre className="text-[11px] text-stone-600 whitespace-pre-wrap font-sans flex-1 line-clamp-5 mb-3">{tpl.body}</pre>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-stone-400">{tpl.platform || 'any'} · used {tpl.useCount}×</span>
                <div className="flex gap-2">
                  <button onClick={() => use(tpl)} className="text-xs text-violet-700 font-medium hover:underline">Use →</button>
                  <button onClick={async () => { await deleteTpl({ id: tpl.id }); toast('Deleted'); refetch({ requestPolicy: 'network-only' }); }}
                    className="text-xs text-red-500 hover:underline">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
